"use client";

import { useEffect, useMemo } from "react";
import cnBind from "classnames/bind";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { OnboardingProgress } from "@/components/Onboarding";
import { Modal } from "@/components/_Modals/Modal";
import { CloseIcon } from "@/shared/assests/svg/svg";
import { createAxiosApi } from "@/shared/axios/axios";
import { Routes } from "@/shared/constants";
import { useOnboardPolling } from "@/shared/hooks/useOnboardPolling";
import type { OnboardFormValues } from "@/shared/types/onboard.types";
import { INDUSTRY_PRESETS, type IndustryPreset } from "@/shared/types/industryPreset";
import type { LeadIntent } from "@/shared/utils/leadIntent";
import { getIndustryPresetByPathname } from "@/shared/utils/industryPresetByPath";
import { buildPublicOnboardPayload, isAlfaPartnerPromo, isConsultationFlow, isOnboardFlow } from "@/shared/utils/onboardSubmit";
import { previewTenantDomain } from "@/shared/utils/tenantDomainPreview";
import { InputText } from "@/shared/ui/_InputText";
import { Button } from "@/shared/ui/Button";
import { CheckBox } from "@/shared/ui/CheckBox";
import { IndustryPresetSelect } from "@/shared/ui/IndustryPresetSelect";

import styles from "./ModalCallBack.module.scss";

const cx = cnBind.bind(styles);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_REGEX = /^[+@]?[\d\s()\-@a-zA-Z_.]{6,40}$/;

type ModalCallBackProps = {
    isOpen: boolean;
    onClose: () => void;
    leadIntent?: LeadIntent;
};

type ModalFormValues = OnboardFormValues & {
    policy: boolean;
};

function isFieldInvalid(touched: boolean, error?: string) {
    return Boolean(touched && error);
}

function resolveInitialIndustryPreset(leadIntent?: LeadIntent): IndustryPreset | "" {
    return leadIntent?.industryPreset ?? getIndustryPresetByPathname(leadIntent?.pagePath ?? "") ?? "";
}

export const ModalCallBack = ({ isOpen, onClose, leadIntent }: ModalCallBackProps) => {
    const { t } = useTranslation("common");
    const m = "home.modalCallBack";
    const onboardPolling = useOnboardPolling();

    const onboardMode = isOnboardFlow(leadIntent?.onboard);
    const consultationMode = isConsultationFlow(leadIntent?.onboard);
    const campaignFlow =
        onboardMode &&
        leadIntent?.onboard?.metadata &&
        "campaign" in leadIntent.onboard.metadata &&
        leadIntent.onboard.metadata.campaign === "BEAUTY_100_2026";

    const validationSchema = useMemo(() => {
        const base = {
            companyName: Yup.string()
                .min(2, t(`${m}.errShort`))
                .max(120, t(`${m}.errLong`))
                .required(t(`${m}.errRequired`)),
            contactEmail: Yup.string()
                .matches(EMAIL_REGEX, t(`${m}.errEmail`))
                .required(t(`${m}.errRequired`)),
            contactName: Yup.string().matches(/^[a-zA-Zа-яА-ЯёЁ\s'-]{0,40}$/, t(`${m}.errName`)),
            contactPhone: Yup.string()
                .trim()
                .matches(CONTACT_REGEX, t(`${m}.errPhone`))
                .required(t(`${m}.errRequired`)),
        };

        return onboardMode
            ? Yup.object({
                  ...base,
                  industryPreset: Yup.string()
                      .oneOf([...INDUSTRY_PRESETS], t(`${m}.errIndustry`))
                      .required(t(`${m}.errIndustry`)),
                  policy: Yup.boolean().oneOf([true], t(`${m}.errPolicy`)),
              })
            : Yup.object(base);
    }, [onboardMode, t]);

    const formik = useFormik<ModalFormValues>({
        initialValues: {
            companyName: "",
            contactEmail: "",
            contactName: "",
            contactPhone: "",
            industryPreset: "",
            promoCode: "",
            policy: false,
        },
        enableReinitialize: false,
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            if (onboardMode && leadIntent?.onboard?.planCode) {
                // Anonymous /api/onboard closed — create only after Telegram-linked cabinet login.
                const q = new URLSearchParams();
                if (values.industryPreset) q.set("industry", String(values.industryPreset).toLowerCase());
                if (leadIntent?.onboard?.planCode) q.set("plan", leadIntent.onboard.planCode);
                const promo =
                    (values.promoCode || "").trim() ||
                    (typeof leadIntent?.onboard?.metadata?.promoCode === "string"
                        ? leadIntent.onboard.metadata.promoCode
                        : "");
                if (promo) q.set("promo", promo.toUpperCase());
                const qs = q.toString();
                const next = `/cabinet/create${qs ? `?${qs}` : ""}`;
                window.location.assign(`/auth?tab=login&next=${encodeURIComponent(next)}`);
                return;
            }

            const tariff = leadIntent?.tariff || t(`${m}.tariffFallback`);
            const body = {
                name: values.contactName || values.companyName,
                email: values.contactEmail || undefined,
                phone: values.contactPhone || values.contactEmail,
                business: consultationMode
                    ? t(`${m}.businessConsultation`, { tariff, company: values.companyName })
                    : t(`${m}.businessLead`, { tariff, company: values.companyName }),
                tariff: leadIntent?.tariff || undefined,
                requestType: leadIntent?.requestType || undefined,
                pagePath: leadIntent?.pagePath || undefined,
                lead_source: leadIntent?.leadSource || undefined,
            };

            try {
                await createAxiosApi()({ type: "post", url: "/mail", body });
                alert(consultationMode ? t(`${m}.successConsultation`) : t(`${m}.successLead`));
                resetForm();
                onClose();
            } catch (err: unknown) {
                const msg =
                    err && typeof err === "object" && "response" in err
                        ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "")
                        : "";
                alert(msg ? t(`${m}.errSubmit`, { msg }) : t(`${m}.errNetwork`));
            }
        },
    });

    useEffect(() => {
        if (!isOpen) {
            formik.resetForm();
            onboardPolling.resetOnboard();
            return;
        }

        const preset = campaignFlow ? "BEAUTY" : resolveInitialIndustryPreset(leadIntent);
        if (preset) {
            void formik.setFieldValue("industryPreset", preset, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, leadIntent?.industryPreset, leadIntent?.pagePath, campaignFlow]);

    const handleClose = () => {
        if (!onboardPolling.isBusy) {
            onClose();
        }
    };

    const handleProgressClose = () => {
        if (onboardPolling.phase === "success") {
            formik.resetForm();
            onClose();
        }
        onboardPolling.resetOnboard();
    };

    const submitLabel = campaignFlow
        ? t(`${m}.submitCampaign`)
        : onboardMode
          ? t(`${m}.submitOnboard`)
          : consultationMode
            ? t(`${m}.submitConsultation`)
            : t(`${m}.submitDefault`);

    const title = campaignFlow
        ? t(`${m}.titleCampaign`)
        : onboardMode
          ? t(`${m}.titleOnboard`)
          : consultationMode
            ? t(`${m}.titleConsultation`)
            : t(`${m}.titleDefault`);

    const domainPreview = previewTenantDomain(formik.values.companyName);
    const canSubmitOnboard = onboardMode
        ? formik.values.policy && Boolean(formik.values.industryPreset) && !onboardPolling.isBusy
        : !onboardPolling.isBusy;

    return (
        <>
            <Modal
                maxWidth="929px"
                isOpen={isOpen}
                onClose={handleClose}
                className={cx("modal-call")}
                contentClassName={`${cx("modalGlassContent")} bg-liquid-glass-panel glass-noise glass-sheet glass-sheet-center`}
            >
                <CloseIcon className={cx("close-icon")} onClick={handleClose} />
                <div className={cx("form-callback", { formCallbackOnboard: onboardMode })}>
                    <div className={cx("title-container")}>
                        <h3>{title}</h3>
                        {!onboardMode && leadIntent?.tariff ? (
                            <div className={cx("intent")}>
                                <span>{t(`${m}.tariffLabel`, { tariff: leadIntent.tariff })}</span>
                            </div>
                        ) : null}
                    </div>
                    <form onSubmit={formik.handleSubmit} className={cx("form", { formOnboard: onboardMode })}>
                        <div className={cx("inputs", { inputsOnboard: onboardMode })}>
                            {onboardMode ? (
                                <div className={cx("inputStack")}>
                                    <InputText
                                        placeholder={t(`${m}.email`)}
                                        type="email"
                                        name="contactEmail"
                                        value={formik.values.contactEmail}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={isFieldInvalid(
                                            Boolean(formik.touched.contactEmail),
                                            formik.errors.contactEmail,
                                        )}
                                        showErrorMessage={false}
                                        isFullWidth
                                        className={cx("input-text", "glass-input-fill")}
                                    />

                                    <div className={cx("companyField")}>
                                        <InputText
                                            placeholder={
                                                campaignFlow ? t(`${m}.companyCampaign`) : t(`${m}.company`)
                                            }
                                            name="companyName"
                                            value={formik.values.companyName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={isFieldInvalid(
                                                Boolean(formik.touched.companyName),
                                                formik.errors.companyName,
                                            )}
                                            showErrorMessage={false}
                                            isFullWidth
                                            className={cx("input-text", "glass-input-fill")}
                                        />
                                        {domainPreview ? (
                                            <div className={cx("slugPreviewSlot")} aria-live="polite">
                                                <span className={cx("slugPreviewValue")}>{domainPreview}</span>
                                            </div>
                                        ) : null}
                                    </div>

                                    <IndustryPresetSelect
                                        value={formik.values.industryPreset ?? ""}
                                        onChange={(preset) => {
                                            void formik.setFieldValue("industryPreset", preset);
                                        }}
                                        onBlur={() => {
                                            void formik.setFieldTouched("industryPreset", true);
                                        }}
                                        invalid={isFieldInvalid(
                                            Boolean(formik.touched.industryPreset),
                                            formik.errors.industryPreset,
                                        )}
                                        disabled={Boolean(campaignFlow)}
                                        placeholder={t(`${m}.industryPlaceholder`)}
                                    />

                                    <InputText
                                        placeholder={t(`${m}.name`)}
                                        name="contactName"
                                        value={formik.values.contactName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={isFieldInvalid(
                                            Boolean(formik.touched.contactName),
                                            formik.errors.contactName,
                                        )}
                                        showErrorMessage={false}
                                        isFullWidth
                                        className={cx("input-text", "glass-input-fill")}
                                    />

                                    <InputText
                                        placeholder={t(`${m}.phone`)}
                                        type="text"
                                        name="contactPhone"
                                        value={formik.values.contactPhone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={isFieldInvalid(
                                            Boolean(formik.touched.contactPhone),
                                            formik.errors.contactPhone,
                                        )}
                                        showErrorMessage={false}
                                        isFullWidth
                                        className={cx("input-text", "glass-input-fill")}
                                    />

                                    <InputText
                                        placeholder={t(`${m}.promo`)}
                                        name="promoCode"
                                        value={formik.values.promoCode ?? ""}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        showErrorMessage={false}
                                        isFullWidth
                                        className={cx("input-text", "glass-input-fill")}
                                    />
                                    {isAlfaPartnerPromo(formik.values.promoCode) ? (
                                        <p className={cx("promoHint")} style={{ margin: 0, fontSize: 13, opacity: 0.85 }}>
                                            {t(`${m}.promoAlfa`)}
                                        </p>
                                    ) : null}
                                </div>
                            ) : (
                                <>
                                    <div className={cx("input")}>
                                        <InputText
                                            placeholder={t(`${m}.company`)}
                                            name="companyName"
                                            value={formik.values.companyName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={isFieldInvalid(
                                                Boolean(formik.touched.companyName),
                                                formik.errors.companyName,
                                            )}
                                            showErrorMessage={false}
                                            isFullWidth
                                            className={cx("input-text", "glass-input-fill")}
                                        />
                                        <InputText
                                            placeholder={t(`${m}.emailDirector`)}
                                            type="email"
                                            name="contactEmail"
                                            value={formik.values.contactEmail}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={isFieldInvalid(
                                                Boolean(formik.touched.contactEmail),
                                                formik.errors.contactEmail,
                                            )}
                                            showErrorMessage={false}
                                            isFullWidth
                                            className={cx("input-text", "glass-input-fill")}
                                        />
                                    </div>
                                    <div className={cx("input")}>
                                        <InputText
                                            placeholder={t(`${m}.nameOptional`)}
                                            name="contactName"
                                            value={formik.values.contactName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={isFieldInvalid(
                                                Boolean(formik.touched.contactName),
                                                formik.errors.contactName,
                                            )}
                                            showErrorMessage={false}
                                            isFullWidth
                                            className={cx("input-text", "glass-input-fill")}
                                        />
                                        <InputText
                                            placeholder={t(`${m}.phoneTelegram`)}
                                            type="text"
                                            name="contactPhone"
                                            value={formik.values.contactPhone}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={isFieldInvalid(
                                                Boolean(formik.touched.contactPhone),
                                                formik.errors.contactPhone,
                                            )}
                                            showErrorMessage={false}
                                            isFullWidth
                                            className={cx("input-text", "glass-input-fill")}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {onboardMode ? (
                            <div className={cx("policyRow")}>
                                <CheckBox
                                    checked={formik.values.policy}
                                    onChange={formik.handleChange}
                                    name="policy"
                                    title={t(`${m}.policyBefore`)}
                                    link={t(`${m}.policyPersonal`)}
                                    url={Routes.POLICY}
                                    titleAfter={t(`${m}.policyAnd`)}
                                    link2={t(`${m}.policyOffer`)}
                                    url2={Routes.TERMS}
                                    titleEnd={t(`${m}.policyEnd`)}
                                />
                            </div>
                        ) : null}

                        <Button
                            type="submit"
                            label={submitLabel}
                            variant="solid"
                            isFullWidth
                            iconTrue={false}
                            disabled={!canSubmitOnboard}
                        />
                    </form>
                </div>
            </Modal>

            <OnboardingProgress
                phase={onboardPolling.phase}
                steps={onboardPolling.steps}
                adminUrl={onboardPolling.adminUrl}
                contactEmail={onboardPolling.contactEmail}
                telegramConfirmUrl={onboardPolling.telegramConfirmUrl}
                telegramBotUsername={onboardPolling.telegramBotUsername}
                onboardMessage={onboardPolling.onboardMessage}
                telegramLinked={onboardPolling.telegramLinked}
                errorMessage={onboardPolling.errorMessage}
                campaignCode={onboardPolling.campaignCode}
                campaignFreeUntil={onboardPolling.campaignFreeUntil}
                onClose={handleProgressClose}
            />
        </>
    );
};
